import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PublicApiService } from '../../services/public-api.service';
import {Fleet} from "../../models/fleet.model";
import {Location} from "../../models/location.model";

declare var bootstrap: any;

interface HubWithLocations {
  hubId: string;
  location: any | null;
  hasError: boolean;
  errorMessage: string;
}

@Component({
  selector: 'app-move-hub',
  templateUrl: './move-hub.component.html',
  styleUrls: ['./move-hub.component.scss']
})
export class MoveHubComponent implements OnInit {
  @Input() selectedFleet: Fleet | null = null;
  @Output() hubMoved = new EventEmitter<void>();

  // Hub search
  hubIdToSearch: string = '';
  searchResults: string[] = [];
  hubsWithLocations: HubWithLocations[] = [];
  searchPerformed: boolean = false;

  selectedLocation: Location | null = null;

  // Status
  statusText: string = '';
  statusTheme: string = 'light';

  // Button status
  findButtonEnabled: boolean = false;

  // Confirmation modal properties
  hubToMove: string = '';
  hubToMoveCurrentLocation: string = '';
  selectedLocationLabel: string = '';

  constructor(private publicApiService: PublicApiService) {}

  ngOnInit(): void {}

  openMoveHubModal(selectedFleet:Fleet|null,selectedLocation:Location|null) {
    // Reset the hub ID and search results
    this.selectedFleet = selectedFleet;
    this.selectedLocation = selectedLocation;
    this.hubIdToSearch = '';
    this.searchResults = [];
    this.searchPerformed = false;

    // Initialize status to light theme
    this.statusText = '';
    this.statusTheme = 'light';

    // Initialize find button as disabled
    this.findButtonEnabled = false;

    // Get the modal element
    const modalElement = document.getElementById('moveHubModal');

    // Create and show the modal
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onHubIdChange() {
    // Enable the Find Hub button if the hub ID is not empty
    this.findButtonEnabled = this.hubIdToSearch.trim().length > 0;

    // Reset search results and status when the hub ID changes
    if (this.searchPerformed) {
      this.searchResults = [];
      this.searchPerformed = false;
      this.statusText = '';
      this.statusTheme = 'light';
    }
  }

  async findHub() {
    if (!this.hubIdToSearch.trim()) {
      return;
    }

    // Update status to 'searching' with 'info' theme
    this.statusText = 'Searching for hubs...';
    this.statusTheme = 'info';

    // Reset hubsWithLocations
    this.hubsWithLocations = [];

    try {
      const response = await this.publicApiService.searchHubs(this.hubIdToSearch);

      if (response && Array.isArray(response) && response.length > 0) {
        this.searchResults = response;
        this.statusText = `Found ${this.searchResults.length} hub(s)`;
        this.statusTheme = 'success';
      } else {
        this.searchResults = [];
        this.statusText = 'No hubs found';
        this.statusTheme = 'warning';
      }
    } catch (error) {
      console.error('Error searching for hubs:', error);
      this.statusText = 'Error searching for hubs. Please try again.';
      this.statusTheme = 'danger';
      this.searchResults = [];
    }

    if(this.searchResults.length > 0 && this.selectedFleet) {
      // Update status to indicate we're fetching locations
      this.statusText = 'Fetching locations for hubs...';
      this.statusTheme = 'info';

      let hasErrors = false;

      for (const hubId of this.searchResults) {
        try {
          const locations = await this.publicApiService.searchFleetHubIdsForLocations(this.selectedFleet.uuid, hubId, 100, 0);

          if (locations && Array.isArray(locations)) {
            if (locations.length === 1) {
              // Add hub with its single location to the hubsWithLocations array
              this.hubsWithLocations.push({
                hubId: hubId,
                location: locations[0],
                hasError: false,
                errorMessage: ''
              });
            } else if (locations.length > 1) {
              // More than one location found - treat as an error
              hasErrors = true;
              this.hubsWithLocations.push({
                hubId: hubId,
                location: null,
                hasError: true,
                errorMessage: `Error: Multiple locations found for this hub (${locations.length})`
              });
            } else {
              // No locations found
              // this.hubsWithLocations.push({
              //   hubId: hubId,
              //   location: null,
              //   hasError: false,
              //   errorMessage: ''
              // });
            }
          } else {
            // No locations found or invalid response
            this.hubsWithLocations.push({
              hubId: hubId,
              location: null,
              hasError: false,
              errorMessage: ''
            });
          }
        } catch (error) {
          console.error(`Error fetching locations for hub ${hubId}:`, error);
          // Add hub with error in case of exception
          hasErrors = true;
          this.hubsWithLocations.push({
            hubId: hubId,
            location: null,
            hasError: true,
            errorMessage: `Error fetching locations: ${error}`
          });
        }
      }

      // Update status after fetching all locations
      if (hasErrors) {
        this.statusText = `Found ${this.searchResults.length} hub(s) but some have errors`;
        this.statusTheme = 'warning';
      } else {
        this.statusText = `Found ${this.hubsWithLocations.length} hub(s) with their locations in fleet: ${this.selectedFleet.id}`;
        this.statusTheme = 'success';
        this.searchPerformed = true;
      }
    }
  }

  closeModal() {
    // Close the modal
    const modalElement = document.getElementById('moveHubModal');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }

    // Reset the component state
    this.hubIdToSearch = '';
    this.searchResults = [];
    this.hubsWithLocations = [];
    this.searchPerformed = false;
    this.statusText = '';
    this.statusTheme = 'light';
    this.findButtonEnabled = false;
  }

  moveHub(hubId: string) {
    console.log(`Preparing to move hub with ID: ${hubId}`);

    // Set the properties for the confirmation dialog
    this.hubToMove = hubId;
    this.selectedLocationLabel = this.selectedLocation?.label || '';
    this.hubToMoveCurrentLocation = this.hubsWithLocations.find(hub => hub.hubId === hubId)?.location?.label || '';

    // Show the confirmation dialog
    const confirmModalElement = document.getElementById('moveHubConfirmModal');
    if (confirmModalElement) {
      const confirmModal = new bootstrap.Modal(confirmModalElement);
      confirmModal.show();
    }
  }

  closeConfirmModal() {
    // Close the confirmation modal
    const confirmModalElement = document.getElementById('moveHubConfirmModal');
    if (confirmModalElement) {
      const confirmModal = bootstrap.Modal.getInstance(confirmModalElement);
      if (confirmModal) {
        confirmModal.hide();
      }
    }
  }

  async confirmMoveHub() {
    // Logic to actually move the hub after confirmation

    if(!this.selectedLocation){
      return;
    }

    console.log(`Moving hub with ID: ${this.hubToMove} from ${this.hubToMoveCurrentLocation} to ${this.selectedLocationLabel}`);

    try {

      const response = await this.publicApiService.updateHubLocation(this.hubToMove, this.selectedLocation.uuid);

    } catch (error)
    {
      console.error(`Error moving hub with ID: ${this.hubToMove} from ${this.hubToMoveCurrentLocation} to ${this.selectedLocationLabel}`, error);
      this.statusText = `Error moving hub with ID: ${this.hubToMove} from ${this.hubToMoveCurrentLocation} to ${this.selectedLocationLabel}`;
      this.statusTheme = 'danger';
      this.searchPerformed = true;
      return;
    }

    // Emit the hubMoved event to notify parent components
    this.hubMoved.emit();

    // Close the confirmation modal
    this.closeConfirmModal();

    // Close the main modal
    this.closeModal();
  }
}
